/**
 * CUSTOM: harempants — Custom JavaScript
 *
 * This file contains custom fixes and enhancements for harempants.com.
 * Loaded via layout/theme.liquid before </body>.
 */

/* ==========================================================================
   Feature: Collapsible "Read more" for collection banner descriptions

   Long collection descriptions push the product grid below the fold. This
   collapses each .collection-hero__description to --crm-lines (3) with a fade
   and a "View more"/"View less" toggle.

   Pairs with the .crm--* / .collection-hero__description rules in custom.css,
   which provide the collapsed clamp + fade styling. This script, per
   description:
     - if the text overflows the line limit  → add .crm--collapsed + a toggle
     - if it fits                            → leave it alone (no toggle/clamp)
   Re-evaluates on resize (text re-wraps at different widths) but never
   re-collapses something the visitor chose to expand. Re-runs on
   shopify:section:load so it works in the theme editor.

   The section wraps the description in a <motion-element> whose fade-up reveal
   starts at translateY(25px). That transform inflates the wrapper's
   scrollHeight, so overflow is measured from the inner element's offsetHeight
   (transform-immune) rather than the wrapper's scroll/clientHeight. Kept first
   in this file so an error in an unrelated IIFE below can't stop it running.
   ========================================================================== */
(function () {
  'use strict';

  var SHOW_TEXT = 'View more';
  var HIDE_TEXT = 'View less';

  function lineHeight(el) {
    return parseFloat(getComputedStyle(el).lineHeight) || 24;
  }

  /* Does the content exceed the line limit? Measure the inner element's
     offsetHeight (layout height, immune to the motion-element's translateY
     transform) rather than the wrapper's scrollHeight, which the transform
     would inflate and cause false positives on short descriptions. */
  function overflows(desc) {
    var lh = lineHeight(desc);
    var lines = parseInt(getComputedStyle(desc).getPropertyValue('--crm-lines'), 10) || 3;
    var content = desc.firstElementChild || desc;
    return content.offsetHeight > lh * lines + Math.max(4, lh * 0.4);
  }

  function makeToggle(desc) {
    var wrap = document.createElement('div');
    wrap.className = 'crm__toggle-wrap';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--underline crm__toggle';
    btn.setAttribute('aria-expanded', 'false');
    if (desc.id) btn.setAttribute('aria-controls', desc.id);
    btn.innerHTML = '<span class="btn__text">' + SHOW_TEXT + '</span>';
    btn.addEventListener('click', function () {
      var expanded = desc.classList.toggle('crm--expanded');
      desc.classList.toggle('crm--collapsed', !expanded);
      btn.setAttribute('aria-expanded', String(expanded));
      btn.querySelector('.btn__text').textContent = expanded ? HIDE_TEXT : SHOW_TEXT;
    });
    wrap.appendChild(btn);
    return wrap;
  }

  function setup(desc) {
    if (!desc) return;
    if (!desc.id) desc.id = 'collection-desc-' + Math.random().toString(36).slice(2, 8);

    var existing = desc.parentElement.querySelector(':scope > .crm__toggle-wrap');
    var wasExpanded = desc.classList.contains('crm--expanded');

    /* Reset to a clean, measured state. */
    desc.classList.remove('crm--ready', 'crm--expanded', 'crm--collapsed');
    var isOverflowing = overflows(desc);
    desc.classList.add('crm--ready');

    if (!isOverflowing) {
      if (existing) existing.remove();
      return;
    }

    var wrap = existing || makeToggle(desc);
    if (!existing) desc.insertAdjacentElement('afterend', wrap);
    var btn = wrap.querySelector('.crm__toggle');

    if (wasExpanded) {
      desc.classList.add('crm--expanded');
      btn.setAttribute('aria-expanded', 'true');
      btn.querySelector('.btn__text').textContent = HIDE_TEXT;
    } else {
      desc.classList.add('crm--collapsed');
      btn.setAttribute('aria-expanded', 'false');
      btn.querySelector('.btn__text').textContent = SHOW_TEXT;
    }
  }

  function setupAll() {
    document.querySelectorAll('.collection-hero__description').forEach(setup);
  }

  function init() {
    if (!document.querySelector('.collection-hero__description')) return;
    setupAll();

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(setupAll, 200);
    });

    document.addEventListener('shopify:section:load', setupAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   Fix: Variant King combined listing swatch does not update ATC button
        or sold-out badge on collection pages
   Test: tests/bugs/variant-king-atc-button.spec.ts

   The Variant King "Combined Listings" app updates title, image, URL, and
   form input when switching color swatches on collection pages — but does
   NOT update:
     - the Add-to-Cart button's disabled state or text
     - the `.product-card__badge > .f-badge--soldout` span's visibility

   This monkey-patch listens for swatch clicks and updates both to match
   the newly selected product's availability.
   ========================================================================== */
(function () {
  'use strict';

  var SOLD_OUT_TEXT = 'Sold out';
  var ADD_TO_CART_TEXT = 'Add to cart';

  /* Determine the "active" swatch for a card.
     - triggerSwatch (click target or hovered element) is preferred — VK may
       not update aria-checked on hover, only on click.
     - Fallbacks: aria-checked="true" li, then .swatch-selected child. */
  function resolveActiveSwatch(card, triggerSwatch) {
    if (triggerSwatch) {
      var li = triggerSwatch.closest('li.swatch-view-item');
      if (li) return li;
    }
    var vk = card.querySelector('variant-king-combined-listing');
    if (!vk) return null;
    var selectedItem = vk.querySelector('li.swatch-view-item[aria-checked="true"]');
    if (selectedItem) return selectedItem;
    var selectedInner = vk.querySelector('.swatch-selected');
    return selectedInner ? selectedInner.closest('li.swatch-view-item') : null;
  }

  function updateCard(card, triggerSwatch) {
    var activeItem = resolveActiveSwatch(card, triggerSwatch);
    if (!activeItem) return;

    var isSoldOut = activeItem.classList.contains('swatch-item-unavailable');

    updateATCButton(card, isSoldOut);
    updateSoldOutBadge(card, isSoldOut);
  }

  function updateATCButton(card, isSoldOut) {
    var btn = card.querySelector('button[name="add"].product-card__atc');
    if (!btn) return;

    var textEl = btn.querySelector('.product-card__atc-text');
    var formInput = card.querySelector('input.product-variant-id, input[name="id"]');

    if (isSoldOut) {
      btn.setAttribute('disabled', '');
      if (formInput) formInput.setAttribute('disabled', '');
      if (textEl) textEl.textContent = SOLD_OUT_TEXT;
    } else {
      btn.removeAttribute('disabled');
      if (formInput) formInput.removeAttribute('disabled');
      if (textEl) textEl.textContent = ADD_TO_CART_TEXT;
    }
  }

  /* Toggle the sold-out badge inside .product-card__badge. The badge container
     can also hold other badges (sale, new, hot, coming) so we only touch the
     .f-badge--soldout span, not the whole container. */
  function updateSoldOutBadge(card, isSoldOut) {
    var soldOutSpan = card.querySelector('.product-card__badge .f-badge--soldout');
    if (!soldOutSpan) return;
    soldOutSpan.style.display = isSoldOut ? '' : 'none';
  }

  function handleSwatchInteraction(e) {
    if (!e.target || !e.target.closest) return;
    var swatch = e.target.closest('.swatch-group-selector');
    if (!swatch) return;

    var card = swatch.closest('.product-card');
    if (!card) return;

    /* Small delay so VK finishes its DOM updates first */
    setTimeout(function () {
      updateCard(card, swatch);
    }, 350);
  }

  /* Delegate clicks — VK updates the DOM then we fix the card */
  document.addEventListener('click', handleSwatchInteraction, true);

  /* Hover-triggered switches. VK's switch_on_hover (when enabled) listens for
     mouseover/mouseout — we match by also listening on mouseover (bubbles).
     mouseenter is kept as a fallback for direct pointer entries. */
  document.addEventListener('mouseover', handleSwatchInteraction, true);
  document.addEventListener('mouseenter', handleSwatchInteraction, true);
})();

/* ==========================================================================
   Feature: Back In Stock notifications on product pages
   Test: tests/bugs/back-in-stock-product-page.spec.ts
   Plan: plans/plan-back-in-stock-product-page.md

   On /products/* pages, any sold-out `.product-form__submit` button is
   rewired into a Back In Stock (Amp) trigger:
     - button text swapped to "Notify me when available"
     - `disabled` removed, `data-bis-proxy="true"` added
     - click is intercepted and forwarded to a hidden `#BIS_trigger` anchor
       that the BIS app binds to

   A single hidden `#BIS_trigger` is appended once. Its `data-variant-id` is
   kept in sync via the theme's pub/sub on variantChange. Works on the desktop
   main buy-button, the mobile sticky ATC bar (standard), and the mobile
   sticky ATC bar (minimalist) — all three share the `.product-form__submit`
   class, so one document-level delegated listener covers all.

   Runs only on product pages (template-product) to avoid false positives on
   card buttons elsewhere.
   ========================================================================== */
(function () {
  'use strict';

  /* Gate: product template only. Covers product.liquid + any variant.
     Hyper emits `product-template` on <body>; accept either naming. */
  var isProductPage =
    document.body.classList.contains('product-template') ||
    document.body.classList.contains('template-product') ||
    /^\/products\//.test(window.location.pathname);
  if (!isProductPage) return;

  var BIS_COPY = 'Notify me when available';
  var BIS_TRIGGER_ID = 'BIS_trigger';
  var PROXY_ATTR = 'data-bis-proxy';
  /* Cache the original ATC label per button so variant swaps restore it. */
  var ORIGINAL_TEXT_ATTR = 'data-bis-original-text';

  function getCurrentVariantId() {
    var input = document.querySelector('product-info [name="id"]');
    return input && input.value ? input.value : '';
  }

  function ensureTrigger() {
    var existing = document.getElementById(BIS_TRIGGER_ID);
    if (existing) return existing;
    var a = document.createElement('a');
    a.id = BIS_TRIGGER_ID;
    a.setAttribute('aria-hidden', 'true');
    a.setAttribute('tabindex', '-1');
    a.href = 'javascript:void(0)';
    a.style.display = 'none';
    a.setAttribute('data-variant-id', getCurrentVariantId());
    document.body.appendChild(a);
    return a;
  }

  function applyOOS(btn) {
    if (btn.getAttribute(PROXY_ATTR) === 'true') return;
    var textEl = btn.querySelector('.btn__text');
    if (textEl && !btn.hasAttribute(ORIGINAL_TEXT_ATTR)) {
      btn.setAttribute(ORIGINAL_TEXT_ATTR, textEl.textContent.trim());
    }
    btn.removeAttribute('disabled');
    btn.setAttribute(PROXY_ATTR, 'true');
    btn.classList.add('product-form__submit--oos');
    if (textEl) textEl.textContent = BIS_COPY;
  }

  function clearOOS(btn) {
    if (btn.getAttribute(PROXY_ATTR) !== 'true') return;
    btn.removeAttribute(PROXY_ATTR);
    btn.classList.remove('product-form__submit--oos');
    var original = btn.getAttribute(ORIGINAL_TEXT_ATTR);
    var textEl = btn.querySelector('.btn__text');
    if (textEl && original) textEl.textContent = original;
    /* Leave `disabled` handling to the theme's own variantChange subscribers;
       we never re-add it ourselves. */
  }

  /* Scan the page and flip each button based on the supplied availability.
     When `available` is undefined (initial load), use the button's `disabled`
     attribute as the signal. */
  function syncAllButtons(available) {
    var buttons = document.querySelectorAll('button.product-form__submit');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      /* Ignore buttons nested in quick-view (own lifecycle, own section). */
      if (btn.closest('quick-view-modal')) continue;
      var soldOut = available === undefined ? btn.hasAttribute('disabled') : !available;
      if (soldOut) {
        applyOOS(btn);
      } else {
        clearOOS(btn);
      }
    }
  }

  /* Capture-phase delegation — runs before the form's submit handler and before
     any sticky-atc-bar click handler, so we cleanly short-circuit submission. */
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('button.product-form__submit[' + PROXY_ATTR + '="true"]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var trigger = document.getElementById(BIS_TRIGGER_ID);
      if (trigger) trigger.click();
    },
    true,
  );

  function subscribeToVariantChange() {
    if (!window.FoxTheme || !window.FoxTheme.pubsub) return false;
    var events = window.FoxTheme.pubsub.PUB_SUB_EVENTS;
    if (!events || !events.variantChange) return false;
    window.FoxTheme.pubsub.subscribe(events.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      var trigger = document.getElementById(BIS_TRIGGER_ID);
      if (trigger && variant) trigger.setAttribute('data-variant-id', String(variant.id));
      /* Defer to next tick so we win the race with the theme's own subscribers
         that may toggle `disabled` on the buttons during the same event. */
      setTimeout(function () {
        syncAllButtons(variant ? variant.available : undefined);
      }, 0);
    });
    return true;
  }

  function init() {
    ensureTrigger();
    syncAllButtons();
    /* FoxTheme loads synchronously in <head> before our defer'd custom.js runs,
       so pubsub is almost always ready. Retry once if not. */
    if (!subscribeToVariantChange()) {
      setTimeout(subscribeToVariantChange, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   GA4 collection-page event tracking has MOVED to assets/custom-cx.js.

   It now detects the same interactions (view_item_list, select_item,
   filter_applied, sort_changed, load_more, no_results) and sends them straight
   to GA4 (Measurement Protocol / g-collect) from page context — superseding the
   retired custom web pixel. See analytics/README.md and
   plans/plan-collection-page-cro.md §4.
   ========================================================================== */
